import { z } from 'zod'
import { pageQuerySchema } from '../pagination'
import { hasControlChars, isIsoDate } from '../validators'

/**
 * Audit API (M11, P-AUD-002/-003, AUD-007). Chains are `platform`, `core` and `org:<orgId>`;
 * which ones a caller may read comes from the policy (`audit.read`, `audit.verify`).
 */
export const AUDIT_CHAIN_PATTERN = /^(platform|core|org:[A-Za-z0-9_-]{1,64})$/

const chainParam = z.string().regex(AUDIT_CHAIN_PATTERN, { error: 'Unknown audit chain.' })

const filterText = z
  .string()
  .max(120)
  .refine((v) => !hasControlChars(v), { error: 'Control characters are not permitted.' })
  .transform((v) => v.trim())

const dateParam = z.string().refine(isIsoDate, { error: 'Use an ISO date (YYYY-MM-DD).' })

export const auditQuerySchema = pageQuerySchema
  .extend({
    chain: chainParam.optional(),
    actor_id: filterText.optional(),
    action: filterText.optional(),
    kind: filterText.optional(),
    record_id: filterText.optional(),
    /** Inclusive UTC dates. */
    from: dateParam.optional(),
    to: dateParam.optional(),
  })
  .refine((v) => !v.from || !v.to || v.from <= v.to, {
    error: 'Start date must not follow end date.',
    path: ['from'],
  })
export type AuditQuery = z.input<typeof auditQuerySchema>

export const auditVerifyQuerySchema = z.object({ chain: chainParam })

export interface AuditEventDto {
  chain: string
  seq: number
  ts: string
  actor_id: string | null
  actor_name: string | null
  impersonated_by: string | null
  request_id: string | null
  action: string
  kind: string
  record_id: string | null
  org_id: string | null
  details: unknown
  prev_hash: string
  hash: string
}

export interface AuditChainInfo {
  chain: string
  /** "Platform", "Fruuit core" or the investor org's name. */
  label: string
}

export interface AuditListResponse {
  items: AuditEventDto[]
  next_cursor: string | null
  /** Chains the caller may read, for the filter. */
  chains: AuditChainInfo[]
}

export interface AuditVerifyResponse {
  chain: string
  ok: boolean
  count: number
  head: { seq: number; hash: string } | null
  failed_at: number | null
  reason: string | null
  verified_at: string
}
